import React, { useEffect, useMemo, useRef, useState } from 'react';
import SparksParentPanel from '../Sparks/SparksParentPanel';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { get } from '../../utils/api';
import { getActiveCoreSkillSession, getDashboardTree, getLessonAttempts } from '../../api/dashboardApi';
import AttemptHistory from './AttemptHistory';
import DashboardSubsection from './DashboardSubsection';
import ModuleRadar from './ModuleRadar';
import MonthlyReports from './MonthlyReports';
import ReviewPromptBanner from '../Review/ReviewPromptBanner';
import WeeklySnapshots from './WeeklySnapshots';

const YEAR_GROUP_OPTIONS = ['Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5', 'Year 6'];
const MODULE_LIMIT = 9;

const toModuleRadarData = (moduleNodes) =>
  (moduleNodes || []).slice(0, MODULE_LIMIT).map((node) => ({
    id: String(node.id),
    label: node.label,
    value: Math.max(0, Math.min(100, Number(node.averagePercent) || 0)),
    statusBand: node.statusBand,
  }));

const getModuleAnchorId = (moduleId) =>
  `dashboard-module-${String(moduleId).replace(/[^a-zA-Z0-9_-]/g, '-')}`;

function Dashboard() {
  const { user, loggedInChild } = useAuth();
  const navigate = useNavigate();

  const [children, setChildren] = useState([]);
  const [selectedChildId, setSelectedChildId] = useState('');
  const [selectedYearGroup, setSelectedYearGroup] = useState('');

  const [nodes, setNodes] = useState([]);
  const [loadingTree, setLoadingTree] = useState(true);
  const [treeError, setTreeError] = useState('');

  const [attemptsByLessonId, setAttemptsByLessonId] = useState({});
  const [loadingAttemptsByLessonId, setLoadingAttemptsByLessonId] = useState({});
  const [activeDashboardSession, setActiveDashboardSession] = useState(null);
  const [previousRadarModules, setPreviousRadarModules] = useState([]);
  const [snapshotLabel, setSnapshotLabel] = useState('');
  const [showRadar, setShowRadar] = useState(true);
  const [highlightedModuleId, setHighlightedModuleId] = useState('');
  const [activeTab, setActiveTab] = useState('progress');
  const highlightTimeoutRef = useRef(null);

  useEffect(() => {
    const fetchChildren = async () => {
      if (!user?._id) return;
      try {
        const response = await get(`/children?userId=${user._id}`, true);
        setChildren(response || []);
      } catch (err) {
        setTreeError('Failed to load children.');
      }
    };
    fetchChildren();
  }, [user?._id]);

  useEffect(() => {
    if (!children.length) return;
    const preferred =
      (loggedInChild?._id && children.find((child) => child._id === loggedInChild._id)) || children[0];
    setSelectedChildId(preferred._id);
    setSelectedYearGroup(preferred.yearGroup || 'Year 1');
  }, [children, loggedInChild?._id]);

  useEffect(() => {
    const fetchTree = async () => {
      if (!selectedChildId) return;
      setLoadingTree(true);
      setTreeError('');
      try {
        const response = await getDashboardTree(selectedChildId, selectedYearGroup);
        const nextNodes = response?.nodes || [];
        const snapshotNodes = response?.snapshotNodes || [];
        setNodes(nextNodes);
        setPreviousRadarModules(toModuleRadarData(snapshotNodes));
        if (response?.snapshotDate) {
          const d = new Date(response.snapshotDate);
          setSnapshotLabel(d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }));
        }
      } catch (err) {
        setTreeError('Failed to load dashboard.');
        setNodes([]);
      } finally {
        setLoadingTree(false);
      }
    };
    fetchTree();
  }, [selectedChildId, selectedYearGroup]);

  useEffect(() => {
    if (!selectedChildId) {
      setActiveDashboardSession(null);
      return undefined;
    }

    let mounted = true;

    const fetchActiveDashboardSession = async () => {
      try {
        const response = await getActiveCoreSkillSession(selectedChildId, {
          source: 'dashboard_progress',
        });
        if (!mounted) return;
        setActiveDashboardSession(response?.session ?? null);
      } catch {
        if (!mounted) return;
        setActiveDashboardSession(null);
      }
    };

    fetchActiveDashboardSession();
    const interval = setInterval(fetchActiveDashboardSession, 30000);
    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [selectedChildId]);

  const selectedChild = useMemo(
    () => children.find((child) => child._id === selectedChildId),
    [children, selectedChildId],
  );
  const radarModules = useMemo(() => toModuleRadarData(nodes), [nodes]);

  useEffect(() => () => {
    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current);
    }
  }, []);

  const handleLeafToggle = async (node, expanded) => {
    if (!expanded || !selectedChildId || !node?.lessonId) return;
    if (attemptsByLessonId[node.lessonId] || loadingAttemptsByLessonId[node.lessonId]) return;

    setLoadingAttemptsByLessonId((prev) => ({ ...prev, [node.lessonId]: true }));
    try {
      const response = await getLessonAttempts(selectedChildId, node.lessonId);
      setAttemptsByLessonId((prev) => ({ ...prev, [node.lessonId]: response?.attempts || [] }));
    } catch (err) {
      setAttemptsByLessonId((prev) => ({ ...prev, [node.lessonId]: [] }));
    } finally {
      setLoadingAttemptsByLessonId((prev) => ({ ...prev, [node.lessonId]: false }));
    }
  };

  const handleSessionStarted = (payload) => {
    const lessonId = payload?.lessonId;
    if (!lessonId) return;
    const params = new URLSearchParams();
    params.set('source', payload?.source || 'dashboard_progress');
    if (payload?.sessionId) params.set('sessionId', payload.sessionId);
    navigate(`/dashboard/core-skill/${lessonId}?${params.toString()}`);
  };

  const handleModuleSelect = (moduleId) => {
    if (!moduleId) return;
    const targetId = getModuleAnchorId(moduleId);
    const targetElement = document.getElementById(targetId);
    if (!targetElement) return;

    targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setHighlightedModuleId(String(moduleId));

    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current);
    }
    highlightTimeoutRef.current = setTimeout(() => {
      setHighlightedModuleId('');
    }, 1200);
  };

  const activeSessionLessonTitle = useMemo(() => {
    if (!activeDashboardSession?.lessonId) return null;
    const targetLessonId = String(activeDashboardSession.lessonId);
    for (const moduleNode of nodes) {
      for (const unitNode of moduleNode.children || []) {
        for (const lessonNode of unitNode.children || []) {
          if (String(lessonNode.lessonId) === targetLessonId) {
            return lessonNode.label;
          }
        }
      }
    }
    return null;
  }, [activeDashboardSession, nodes]);

  if (loadingTree && !selectedChildId) {
    return (
      <div className="container mx-auto p-4">
        <div className="py-10 flex items-center gap-2 text-gray-600">
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
          <span>Loading dashboard...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4">
      <ReviewPromptBanner />
      <div className="mb-4 pb-2 border-b border-gray-200">
        <h1 className="text-2xl font-semibold text-foreground">Progress Dashboard</h1>
      </div>

      <div className="mb-4 flex gap-2 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('progress')}
          className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'progress'
              ? 'border-indigo-600 text-indigo-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Progress
        </button>
        <button
          onClick={() => setActiveTab('monthly-reports')}
          className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'monthly-reports'
              ? 'border-indigo-600 text-indigo-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Monthly Reports
        </button>
        <button
          onClick={() => setActiveTab('weekly-snapshots')}
          className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'weekly-snapshots'
              ? 'border-indigo-600 text-indigo-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Weekly Snapshots
        </button>
        <button
          onClick={() => setActiveTab('sparks')}
          className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'sparks'
              ? 'border-indigo-600 text-indigo-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Sparks
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <div>
          <label className="block text-xs text-gray-600 mb-1">Child</label>
          <select
            className="w-full border rounded-md p-2 text-sm"
            value={selectedChildId}
            onChange={(e) => {
              const nextChildId = e.target.value;
              const nextChild = children.find((child) => child._id === nextChildId);
              setSelectedChildId(nextChildId);
              setSelectedYearGroup(nextChild?.yearGroup || 'Year 1');
              setAttemptsByLessonId({});
              setLoadingAttemptsByLessonId({});
            }}
          >
            {children.map((child) => (
              <option key={child._id} value={child._id}>
                {child.name}
              </option>
            ))}
          </select>
        </div>
        {activeTab === 'progress' && (
          <div>
            <label className="block text-xs text-gray-600 mb-1">Year group</label>
            <select
              className="w-full border rounded-md p-2 text-sm"
              value={selectedYearGroup}
              onChange={(e) => {
                setSelectedYearGroup(e.target.value);
                setAttemptsByLessonId({});
                setLoadingAttemptsByLessonId({});
              }}
            >
              {YEAR_GROUP_OPTIONS.map((yearGroup) => (
                <option key={yearGroup} value={yearGroup}>
                  {yearGroup}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {activeTab === 'monthly-reports' && (
        <MonthlyReports childId={selectedChildId} />
      )}

      {activeTab === 'sparks' && (
        <SparksParentPanel childId={selectedChildId} />
      )}

      {activeTab === 'weekly-snapshots' && (
        <WeeklySnapshots childId={selectedChildId} />
      )}

      {activeTab === 'progress' && (
        <>
          <div className="mb-4 p-3 rounded-lg bg-blue-50 border border-blue-100 text-blue-900 text-sm">
            For any red or amber topic, practice the core skill first, then retry the lesson in View
            Lessons.
          </div>

          {!loadingTree && !treeError && radarModules.length > 0 ? (
            <div className="mb-2 flex justify-end">
              <button
                type="button"
                onClick={() => setShowRadar((previous) => !previous)}
                className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300"
                aria-expanded={showRadar}
                aria-controls="module-radar-panel"
              >
                {showRadar ? 'Collapse view' : 'Expand view'}
              </button>
            </div>
          ) : null}

          {showRadar && !loadingTree && !treeError && radarModules.length > 0 ? (
            <div id="module-radar-panel">
              <ModuleRadar
                modules={radarModules}
                previousModules={previousRadarModules}
                snapshotLabel={snapshotLabel}
                onModuleSelect={handleModuleSelect}
              />
            </div>
          ) : null}

          {activeDashboardSession ? (
            <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-sm">
              <p className="font-medium">
                A dashboard core skill is already in progress
                {activeSessionLessonTitle ? `: ${activeSessionLessonTitle}` : '.'}
              </p>
              <p className="text-xs mt-1">
                Start another dashboard core skill only after this one is completed or one hour has
                passed.
              </p>
            </div>
          ) : null}

          {loadingTree ? (
            <div className="py-10 flex items-center gap-2 text-gray-600">
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
              <span>Loading dashboard...</span>
            </div>
          ) : null}

          {!loadingTree && treeError ? (
            <div className="p-3 rounded-lg border border-red-200 bg-red-50 text-red-700">{treeError}</div>
          ) : null}

          {!loadingTree && !treeError && selectedChild && nodes.length === 0 ? (
            <div className="p-3 rounded-lg border bg-gray-50 text-gray-700 text-sm">
              No dashboard data available yet.
            </div>
          ) : null}

          {!loadingTree && !treeError ? (
            <div className="space-y-2">
              {nodes.map((node) => (
                <div
                  key={node.id}
                  id={getModuleAnchorId(node.id)}
                  className={`scroll-mt-24 rounded-xl transition-shadow duration-300 ${
                    highlightedModuleId === String(node.id)
                      ? 'shadow-[0_0_0_2px_rgba(92,59,209,0.35)]'
                      : ''
                  }`}
                >
                  <DashboardSubsection
                    node={node}
                    defaultExpanded={false}
                    onLeafToggle={handleLeafToggle}
                    childId={selectedChildId}
                    onSessionStarted={handleSessionStarted}
                    activeDashboardSession={activeDashboardSession}
                    renderLevel3Extras={(leafNode) => (
                      <AttemptHistory
                        attempts={attemptsByLessonId[leafNode.lessonId] || []}
                        loading={!!loadingAttemptsByLessonId[leafNode.lessonId]}
                      />
                    )}
                  />
                </div>
              ))}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

export default Dashboard;
